import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { DialogService } from '@progress/kendo-angular-dialog';
import { NotificationService } from '@progress/kendo-angular-notification';
import moment from 'moment';
import {
  Observable,
  catchError,
  combineLatest,
  debounceTime,
  of,
  startWith,
  takeUntil,
} from 'rxjs';
import { SCHEDULE_MEETING_CONSTANTS } from 'src/app/modules/student-info/schedule-meeting/schedule-meeting-constants';
import { CommonService } from 'src/app/services/common.service';
import { LoadingService } from 'src/app/services/loading.service';
import { ReferenceService } from 'src/app/services/reference.service';
import { StudentService } from 'src/app/services/student.service';
import { AbstractForm } from 'src/app/shared/components/abstract-form';
import { IReferenceData } from 'src/app/shared/interfaces/form/reference-data.model';
import { ISedmsFormModel } from 'src/app/shared/interfaces/form/sedms-form-model';
import { AddStudentProfileFormModel } from 'src/app/shared/models/edit-student-profile-model/student-profile-formmodel';
import { StudentProfileResponseModel } from 'src/app/shared/models/edit-student-profile-model/student-profile-response-model';
import { StudentProfileSubmitModel } from 'src/app/shared/models/edit-student-profile-model/submit-student-profile-model';
import { User } from 'src/app/shared/models/user';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { AuthStore } from 'src/app/stores/auth.store';
import { ReferenceStore } from 'src/app/stores/reference.store';
import {
  BRONX_BOROUGH,
  DEBOUNCE_TIME,
  INDEX_FOUND,
  INDEX_NOT_FOUND,
  NEW_YORK_STATE_ID,
  PARENT_REFUSED_TO_SIGN,
  STATEN_ISLAND_BOROUGH,
} from '../../constants/edit-student-profile';
import {
  HOURS,
  MINUTES,
  MS,
  SECONDS,
  TWENTY_ONE_YEARS_AGO_COUNT,
  TWO_YEARS_AGO_COUNT,
} from '../../constants/time-constants';
import { StudentProfile } from '../../interfaces/student-profile';
import { GeoClientResponse } from '../../interfaces/goat-address';
import { SVGIcon } from '@progress/kendo-svg-icons';
import { RoutesConstantService } from 'src/app/services/routes-constant.service';
import { ApiResponse } from '../../interfaces/api-response';
import { EditStudentSubmitResponse } from 'src/app/modules/student-search/models/student-search-interface';
import { StudentDetails } from '../../interfaces/student';

@Component({
  selector: 'app-edit-student-profile',
  templateUrl: './edit-student-profile.component.html',
  styleUrls: ['./edit-student-profile.component.scss'],
  standalone: false,
})
export class EditStudentProfileComponent extends AbstractForm<
  StudentProfileResponseModel,
  any,
  Record<string, IReferenceData[]>
> {
  @Output() closeDialog: EventEmitter<void> = new EventEmitter<void>();
  @Input() studentDetails: StudentDetails;
  threeYearsAgo: Date = new Date();
  twentyOneYearsAgo: Date = new Date();
  timePickerValue: Date = SCHEDULE_MEETING_CONSTANTS.timePickerDefaultValue;
  showPreviousAttemptsBtn: boolean;
  dropDownValues: IReferenceData[];
  loadSpinner: boolean;
  loadSpinners: boolean;
  showToPhone: boolean;
  showToEmail: boolean;
  showToPostal: boolean;
  showFromEmail: boolean;
  showFromPhone: boolean;
  showFromPostal: boolean;
  studentSk: string;
  todayDate: Date = new Date();
  dateTimeOccured: string;
  canTogleSelected: boolean = true;
  studentRelatedEvent: string;
  studentRelatedEventFormJson: string;

  iepFormSk: string;
  user: User;
  finalizeShown: boolean = true;
  form: FormGroup;
  formModel: ISedmsFormModel = AddStudentProfileFormModel;
  showOtherInput: boolean;
  locked: boolean = false;
  userDetails: User;
  ethnicityDropdown: IReferenceData[];

  constructor(
    private authStore: AuthStore,
    notificationService: NotificationService,
    private studentService: StudentService,
    private referenceService: ReferenceService,
    private referenceStore: ReferenceStore,
    dialogService: DialogService,
    cdRef: ChangeDetectorRef,
    route: ActivatedRoute,
    formBuilder: FormBuilder,
    public activatedRoute: ActivatedRoute,
    private commonService: CommonService,
    private routesContantService: RoutesConstantService,
    private router: Router,
    loadingService: LoadingService
  ) {
    super(
      loadingService,
      cdRef,
      route,
      formBuilder,
      dialogService,
      notificationService
    );
    this.user = this.authStore.UserInfo;
    this.authStore.UserInfo$.pipe(takeUntil(this.componentDestroyed)).subscribe(
      (userInfo: User) => {
        this.userDetails = userInfo;
      }
    );
    this.threeYearsAgo.setFullYear(
      this.todayDate.getFullYear() - TWO_YEARS_AGO_COUNT
    );
    this.twentyOneYearsAgo.setFullYear(
      this.todayDate.getFullYear() - TWENTY_ONE_YEARS_AGO_COUNT
    );
    this.twentyOneYearsAgo.setHours(HOURS, MINUTES, SECONDS, MS);
    this.loadSpinner = true;
  }

  get entity$(): Observable<StudentProfileResponseModel> {
    const obj: StudentProfileResponseModel = new StudentProfileResponseModel(
      this.studentDetails as any,
      this.referenceData$ as any
    );
    return of(obj);
  }
  get referenceData$(): Observable<IReferenceData[][]> {
    return combineLatest([
      this.referenceStore.state$,
      this.referenceStore.language$,
      this.referenceStore.borough$,
      this.referenceStore.district$,
      this.referenceStore.ethnicity$,
      this.referenceStore.sex$,
      this.referenceStore.language$,
      this.referenceStore.grade$,
    ]);
  }

  customFaIcon(icon: IconDefinition): SVGIcon {
    return {
      name: icon.iconName,
      content: `<path d="${icon.icon[4]}" />`,
      viewBox: `0 0 ${icon.icon[0]} ${icon.icon[1]}`,
    };
  }

  //get dropdown details data and entity get by Id Data
  initEntity(
    entityData: unknown,
    referenceData: Record<string, IReferenceData[]>
  ): StudentProfileResponseModel {
    return new StudentProfileResponseModel(this.studentDetails as any, referenceData);
  }

  initReferenceData([state, language, borough, district, ethnicity, sex]: [
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
    IReferenceData[],
    IReferenceData[]
  ]): Record<string, IReferenceData[]> {
    // Filter out "Parent Refused to Sign" and sort by `value`
    const filteredEthnicity: IReferenceData[] = ethnicity
      .filter(
        (item: IReferenceData) => item.description !== PARENT_REFUSED_TO_SIGN
      )
      .sort((a: IReferenceData, b: IReferenceData) =>
        a.value > b.value ? INDEX_FOUND : INDEX_NOT_FOUND
      );

    return {
      state,
      language,
      borough,
      district,
      ethnicity: filteredEthnicity,
      sex,
    };
  }

  override readonly isDialog: boolean = true;

  initWatches(): void {
    combineLatest([
      this.form.get('borough')?.valueChanges.pipe(
        startWith(this.form.get('borough')?.value),
        catchError(() => of(''))
      ),
      this.form.get('streetNumber')?.valueChanges.pipe(
        startWith(this.form.get('streetNumber')?.value),
        catchError(() => of(''))
      ),
      this.form.get('streetName')?.valueChanges.pipe(
        startWith(this.form.get('streetName')?.value),
        catchError(() => of(''))
      ),
    ])
      .pipe(debounceTime(DEBOUNCE_TIME), takeUntil(this.componentDestroyed))
      .subscribe(
        ([borough, streetNumber, streetName]: [
          IReferenceData,
          string,
          string
        ]) => {
          if (
            borough &&
            streetNumber &&
            streetName &&
            Number(borough?.id) >= BRONX_BOROUGH &&
            Number(borough?.id) <= STATEN_ISLAND_BOROUGH
          ) {
            this.commonService
              .getGOATAddress(borough?.description, streetNumber, streetName)
              .pipe(takeUntil(this.componentDestroyed))
              .subscribe((response: GeoClientResponse) => {
                if (!response?.address?.message) {
                  this.form
                    .get('city')
                    .setValue(
                      response?.address?.uspsPreferredCityName?.trim() ?? ''
                    );
                  this.form.get('city').updateValueAndValidity();
                  this.form
                    .get('zip')
                    .setValue(response?.address?.zipCode?.trim() ?? '');
                  this.form.get('zip').updateValueAndValidity();
                  this.form
                    .get('district')
                    .setValue(
                      this.referenceData.district.find(
                        (data: IReferenceData) =>
                          Number(data?.id) ===
                          Number(
                            response?.address?.communitySchoolDistrict?.trim()
                          )
                      )
                    );
                  this.form.get('district').updateValueAndValidity();
                }
              });
          }
        }
      );

    this.form
      .get('borough')
      ?.valueChanges.pipe(startWith(this.form.get('borough')?.value))
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((value: IReferenceData) => {
        if (
          (value?.id as number) >= BRONX_BOROUGH &&
          (value?.id as number) <= STATEN_ISLAND_BOROUGH
        ) {
          this.form
            .get('state')
            .setValue(
              this.referenceData.state.find(
                (data: IReferenceData) => data?.id === NEW_YORK_STATE_ID
              )
            );
          this.form.get('state').updateValueAndValidity();
        }
      });
  }

  onDOBChange(): void {
    const value: Date = this.form?.value?.dateOfBirth;
    if (value) {
      const age: number = moment().diff(value, 'years', false);
      this.form.get('age').patchValue(age);
    } else {
      this.form.get('age').patchValue(null);
    }
  }

  public override otherInitalizations(): void {
    this.form?.get('dateOfBirth').markAsTouched();
  }

  public override afterOnDestroy(): void {
    // afterOnDestroy
  }

  postCancelConfirmation(): void {
    this.loadSpinners = false;
  }

  postSubmitConfirmation(): void {
    this.closeDialog.next();
  }

  closeDailog(): void {
    this.closeDialog.next();
  }

  public splitBeforeAndAfterFirstSpace(input: string): {
    before: string;
    after: string;
  } {
    const parts: string[] = input ? input.trim().split(' ') : [];
    const [before, ...rest] = parts;
    const after: string = rest.join(' ');
    return { before, after };
  }

  saveDetails(): Observable<ApiResponse<EditStudentSubmitResponse>> {
    const payload: StudentProfileSubmitModel = new (StudentProfileSubmitModel as any)(
      {
        ...this.form.value,
      },
      this.referenceData
    );
    payload.student.id = this.studentDetails.nycId;
    payload.submittedBy = this.userDetails.username;
    return this.studentService.editStudent(payload);
  }

  get canSaveDraft(): boolean {
    return !this.form.valid;
  }

  readonly canSubmit: boolean = false;
}
